import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ModuloZoomSheet } from "./ModuloZoomSheet";
import { Loader2, Save, PenLine, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { usePageImages, SignaturePad, FIELD_COLORS } from "./ModuloShared";

const SIG = { signature_tecnico: ["tecnico", "Firma del sopralluoghista"], signature_cliente: ["cliente", "Firma del cliente"] };

const useIsMobile = () => {
  const [m, setM] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const q = window.matchMedia("(max-width: 767px)");
    const h = (e) => setM(e.matches);
    q.addEventListener("change", h);
    return () => q.removeEventListener("change", h);
  }, []);
  return m;
};

const MobileField = ({ f, value, signed, locked, onOpen, sigUrl }) => {
  const sig = SIG[f.type];
  const done = sig ? signed.includes(sig[0]) : !!value;
  const c = sig && done ? "#2E7D32" : FIELD_COLORS[f.type];
  return (
    <button type="button" disabled={locked} onClick={onOpen} className="absolute overflow-hidden text-left leading-none rounded-[2px] px-[1px]" data-testid={`modulo-field-${f.id}`}
      style={{ left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%`, border: `1px solid ${c}`, background: done ? `${c}26` : "#E3F2FD99", color: "#0b1f5c", fontSize: 7 }}>
      {sig ? (done ? (sigUrl ? <img src={sigUrl} alt="Firma" className="w-full h-full object-contain pointer-events-none" /> : "✓") : "") : f.type === "checkbox" ? (value ? "✓" : "") : <span className="whitespace-nowrap">{value || ""}</span>}
    </button>
  );
};

const FieldInput = ({ f, value, onChange, locked, signed, onSign, sigUrl }) => {
  const style = { left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%` };
  if (SIG[f.type]) {
    const ok = signed.includes(SIG[f.type][0]);
    return (
      <button type="button" disabled={locked} onClick={() => onSign(f.type)} className="absolute flex items-center justify-center gap-1 text-[10px] md:text-xs font-bold rounded"
        style={{ ...style, border: `2px dashed ${ok ? "#2E7D32" : FIELD_COLORS[f.type]}`, background: ok ? "#2E7D3215" : "#fff9", color: ok ? "#2E7D32" : FIELD_COLORS[f.type] }} data-testid={`modulo-sign-${SIG[f.type][0]}`}>
        {ok ? (sigUrl ? <img src={sigUrl} alt="Firma" className="w-full h-full object-contain pointer-events-none" data-testid={`modulo-sign-img-${SIG[f.type][0]}`} /> : <><CheckCircle2 className="w-3.5 h-3.5" /> Firmato</>) : <><PenLine className="w-3.5 h-3.5" /> {SIG[f.type][1]}</>}
      </button>
    );
  }
  if (f.type === "checkbox") {
    return <input type="checkbox" checked={!!value} disabled={locked} onChange={(e) => onChange(e.target.checked)} className="absolute m-0 accent-blue-800" style={{ ...style, minWidth: 20, minHeight: 20 }} title={f.label} data-testid={`modulo-field-${f.id}`} />;
  }
  return (
    <input type={f.type === "date" ? "date" : "text"} value={value || ""} disabled={locked} onChange={(e) => onChange(e.target.value)} placeholder={f.label}
      className="absolute px-1 text-base md:text-sm rounded-sm min-h-[26px] md:min-h-0 outline-none focus:ring-2 focus:ring-blue-500" title={f.label}
      style={{ ...style, background: "#E3F2FDcc", color: "#0b1f5c", border: "1px solid #90CAF9" }} data-testid={`modulo-field-${f.id}`} />
  );
};

export const ModuloCompila = ({ cid, onChanged, locked: lockedProp }) => {
  const [st, setSt] = useState(undefined);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [signing, setSigning] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [sigUrls, setSigUrls] = useState({});
  const [sigVer, setSigVer] = useState(0);
  const urls = usePageImages(st?.template);
  const mobile = useIsMobile();
  const ordered = useMemo(() => [...(st?.template?.fields || [])].sort((a, b) => a.page - b.page || (Math.abs(a.y - b.y) < 0.008 ? a.x - b.x : a.y - b.y)), [st?.template?.fields]);

  const load = useCallback(() => api.get(`/clienti/${cid}/modulo`).then(({ data }) => { setSt(data); setValues(data.values || {}); }).catch(() => setSt(null)), [cid]);
  useEffect(() => { load(); }, [load, lockedProp]);
  const sigKey = `${(st?.signed || []).join(",")}|${sigVer}`;
  useEffect(() => {
    const roles = st?.signed || [];
    let alive = true;
    const made = [];
    Promise.all(roles.map((r) => api.get(`/clienti/${cid}/modulo/signature/${r}`, { responseType: "blob", params: { v: sigVer } })
      .then(({ data }) => { const u = URL.createObjectURL(data); made.push(u); return [r, u]; }).catch(() => [r, ""])))
      .then((pairs) => { if (alive) setSigUrls(Object.fromEntries(pairs)); });
    return () => { alive = false; made.forEach((u) => URL.revokeObjectURL(u)); };
  }, [cid, sigKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (st === undefined) return <div className="py-3"><Loader2 className="w-4 h-4 animate-spin" /></div>;
  if (!st?.template) return null;
  const tpl = st.template;

  const save = async () => {
    setSaving(true);
    try { await api.put(`/clienti/${cid}/modulo`, { values }); toast.success("Modulo salvato"); await load(); onChanged?.(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  const saveSig = async (dataUrl) => {
    setSaving(true);
    try {
      await api.put(`/clienti/${cid}/modulo`, { values });
      await api.put(`/clienti/${cid}/modulo/signature`, { role: SIG[signing][0], data_url: dataUrl });
      toast.success("Firma salvata"); setSigning(null); setSigVer((v) => v + 1); await load(); onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <div className="mb-3 space-y-3" data-testid="modulo-compila">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm font-semibold flex-1">Modulo della commessa · {tpl.name}</div>
        {st.saved_at && <span className="text-xs" style={{ color: "var(--text-muted)" }} data-testid="modulo-saved-at">Salvato {new Date(st.saved_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>}
        {!st.locked && <button onClick={save} disabled={saving} className="btn-primary" data-testid="modulo-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva modulo</button>}
      </div>
      {st.missing_signatures.length > 0 && (
        <div className="text-xs p-2 rounded" style={{ background: "#FFF3E0", color: "#8D3B00" }} data-testid="modulo-missing-signatures">Per completare la sezione mancano le firme: {st.missing_signatures.join(", ")}</div>
      )}
      <div className="space-y-4 overflow-x-auto">
        {tpl.pages.map((_, i) => (
          <div key={i} className="relative border shadow-sm max-w-3xl min-w-[320px]" data-testid={`modulo-page-${i}`}>
            {urls[i] ? <img src={urls[i]} alt={`Pagina ${i + 1}`} className="w-full block" draggable={false} /> : urls.length ? <div className="h-96 flex items-center justify-center text-sm" style={{ color: "#B30000" }}>Pagina non caricata: ricarica la pagina</div> : <div className="h-96 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>}
            {tpl.fields.filter((f) => f.page === i).map((f) => mobile ? (
              <MobileField key={f.id} f={f} value={values[f.id]} signed={st.signed} locked={st.locked} sigUrl={sigUrls[SIG[f.type]?.[0]]} onOpen={() => setZoom(ordered.findIndex((o) => o.id === f.id))} />
            ) : (
              <FieldInput key={f.id} f={f} value={values[f.id]} locked={st.locked} signed={st.signed} onSign={setSigning} sigUrl={sigUrls[SIG[f.type]?.[0]]}
                onChange={(v) => setValues((cur) => ({ ...cur, [f.id]: v }))} />
            ))}
          </div>
        ))}
      </div>
      {mobile && !st.locked && <div className="text-xs" style={{ color: "var(--text-muted)" }}>Tocca un campo per compilarlo: usa Avanti per passare al successivo.</div>}
      {zoom !== null && ordered[zoom] && (
        <ModuloZoomSheet fields={ordered} idx={zoom} url={urls[ordered[zoom].page]} values={values} signed={st.signed} sigUrl={sigUrls[SIG[ordered[zoom].type]?.[0]]} sigKeyOf={(t) => SIG[t]?.[0]}
          onChange={(id, v) => setValues((cur) => ({ ...cur, [id]: v }))} onSign={setSigning} onGo={setZoom} onClose={() => setZoom(null)} />
      )}
      {signing && <SignaturePad title={SIG[signing][1]} saving={saving} onClose={() => setSigning(null)} onSave={saveSig} />}
    </div>
  );
};
